export interface NameFeatures {
  isMasculine: boolean;
  translation: string;
  pluralTranslation?: string;
  isWithoutArticle?: boolean;
  isPlural?: boolean;
}

export enum FormName {
  host,
  site,
  city,
  maintainer,
  perimeter,
  owner,
  role,
  provider,
  network_type,
  interface,
  measure,
  scenario,
  script,
  delegated_script,
  credential_group,
  image_group,
  shared_action_group,
  application,
  smartAlert,
  event,
  template,
  maintenance,
  analysis_range,
  user,
  contact,
  contact_group,
  functionality_group,
  view_group,
  view,
  multiple_views,
  api_key,
  dashboard,
  schedule_report,
  dashView,
  background,
  probe,
  flow_report,
  flow_pool,
  ip_service,
  subnets,
  tos_alias,
  measure_type,
  result_type,
  label,
  site_type,
  dataset,
  agent_configuration,
  playlist,
  incident,
}

export const formNamesFeatures: Record<FormName, NameFeatures> = {
  [FormName.host]: {
    isMasculine: true,
    translation: $localize`host`,
    pluralTranslation: $localize`hosts`,
  },
  [FormName.site]: {
    isMasculine: true,
    translation: $localize`site`,
    pluralTranslation: $localize`sites`,
  },
  [FormName.city]: {
    isMasculine: false,
    translation: $localize`city`,
    pluralTranslation: $localize`cities`,
  },
  [FormName.maintainer]: {
    isMasculine: true,
    translation: $localize`maintainer`,
    pluralTranslation: $localize`maintainers`,
  },
  [FormName.perimeter]: {
    isMasculine: true,
    translation: $localize`perimeter`,
    pluralTranslation: $localize`perimeters`,
  },
  [FormName.owner]: {
    isMasculine: true,
    translation: $localize`owner`,
    pluralTranslation: $localize`owners`,
  },
  [FormName.role]: {
    isMasculine: true,
    translation: $localize`role`,
    pluralTranslation: $localize`roles`,
  },
  [FormName.provider]: {
    isMasculine: true,
    translation: $localize`provider`,
    pluralTranslation: $localize`providers`,
  },
  [FormName.network_type]: {
    isMasculine: true,
    translation: $localize`network type`,
    pluralTranslation: $localize`network types`,
  },
  [FormName.interface]: {
    isMasculine: false,
    translation: $localize`interface`,
    pluralTranslation: $localize`interfaces`,
  },
  [FormName.measure]: {
    isMasculine: false,
    translation: $localize`measure`,
    pluralTranslation: $localize`measures`,
  },
  [FormName.scenario]: {
    isMasculine: true,
    translation: $localize`scenario`,
    pluralTranslation: $localize`scenarios`,
  },
  [FormName.script]: {
    isMasculine: true,
    translation: $localize`script`,
    pluralTranslation: $localize`scripts`,
  },
  [FormName.delegated_script]: {
    isMasculine: true,
    translation: $localize`delegated script`,
    pluralTranslation: $localize`delegated scripts`,
  },
  [FormName.credential_group]: {
    isMasculine: true,
    translation: $localize`credential group`,
    pluralTranslation: $localize`credential groups`,
  },
  [FormName.image_group]: {
    isMasculine: true,
    translation: $localize`image group`,
    pluralTranslation: $localize`image groups`,
  },
  [FormName.shared_action_group]: {
    isMasculine: true,
    translation: $localize`shared action group`,
    pluralTranslation: $localize`shared action groups`,
  },
  [FormName.application]: {
    isMasculine: false,
    translation: $localize`application`,
    pluralTranslation: $localize`applications`,
  },
  [FormName.smartAlert]: {
    isMasculine: false,
    translation: $localize`smartAlert`,
    pluralTranslation: $localize`smartAlerts`,
  },
  [FormName.event]: {
    isMasculine: true,
    translation: $localize`event`,
    pluralTranslation: $localize`events`,
  },
  [FormName.template]: {
    isMasculine: true,
    translation: $localize`template`,
    pluralTranslation: $localize`templates`,
  },
  [FormName.maintenance]: {
    isMasculine: false,
    translation: $localize`maintenance`,
    pluralTranslation: $localize`maintenances`,
  },
  [FormName.analysis_range]: {
    isMasculine: false,
    translation: $localize`analysis range`,
    pluralTranslation: $localize`analysis ranges`,
  },
  [FormName.user]: {
    isMasculine: true,
    translation: $localize`user`,
    pluralTranslation: $localize`users`,
  },
  [FormName.contact]: {
    isMasculine: true,
    translation: $localize`contact`,
    pluralTranslation: $localize`contacts`,
  },
  [FormName.contact_group]: {
    isMasculine: true,
    translation: $localize`contact group`,
    pluralTranslation: $localize`contact groups`,
  },
  [FormName.functionality_group]: {
    isMasculine: true,
    translation: $localize`functionality group`,
    pluralTranslation: $localize`functionality groups`,
  },
  [FormName.view_group]: {
    isMasculine: true,
    translation: $localize`view group`,
    pluralTranslation: $localize`view groups`,
  },
  [FormName.view]: {
    isMasculine: false,
    translation: $localize`view`,
    pluralTranslation: $localize`views`,
  },
  [FormName.multiple_views]: {
    isMasculine: true,
    translation: $localize`multiple views`,
    isWithoutArticle: true,
    isPlural: true,
  },
  [FormName.api_key]: {
    isMasculine: false,
    translation: $localize`api key`,
    pluralTranslation: $localize`api keys`,
  },
  [FormName.dashboard]: {
    isMasculine: true,
    translation: $localize`dashboard`,
    pluralTranslation: $localize`dashboards`,
  },
  [FormName.schedule_report]: {
    isMasculine: true,
    translation: $localize`scheduled report`,
    pluralTranslation: $localize`scheduled reports`,
  },
  [FormName.dashView]: {
    isMasculine: true,
    translation: $localize`dashView`,
    pluralTranslation: $localize`dashViews`,
  },
  [FormName.background]: {
    isMasculine: true,
    translation: $localize`background`,
    pluralTranslation: $localize`backgrounds`,
  },
  [FormName.probe]: {
    isMasculine: false,
    translation: $localize`probe`,
    pluralTranslation: $localize`probes`,
  },
  [FormName.flow_report]: {
    isMasculine: true,
    translation: $localize`flow report`,
    pluralTranslation: $localize`flow reports`,
  },
  [FormName.flow_pool]: {
    isMasculine: true,
    translation: $localize`flow pool`,
    pluralTranslation: $localize`flow pools`,
  },
  [FormName.ip_service]: {
    isMasculine: true,
    translation: $localize`IP service`,
    pluralTranslation: $localize`IP services`,
  },
  [FormName.subnets]: {
    isMasculine: true,
    translation: $localize`subnets`,
    isPlural: true,
  },
  [FormName.tos_alias]: {
    isMasculine: true,
    translation: $localize`TOS alias`,
    pluralTranslation: $localize`TOS aliases`,
  },
  [FormName.measure_type]: {
    isMasculine: true,
    translation: $localize`measure type`,
    pluralTranslation: $localize`measure types`,
  },
  [FormName.result_type]: {
    isMasculine: true,
    translation: $localize`result type`,
    pluralTranslation: $localize`result types`,
  },
  [FormName.label]: {
    isMasculine: true,
    translation: $localize`label`,
    pluralTranslation: $localize`labels`,
  },
  [FormName.site_type]: {
    isMasculine: true,
    translation: $localize`site type`,
    pluralTranslation: $localize`site types`,
  },
  [FormName.dataset]: {
    isMasculine: true,
    translation: $localize`dataset`,
    pluralTranslation: $localize`datasets`,
  },
  [FormName.agent_configuration]: {
    isMasculine: false,
    translation: $localize`agent configuration`,
    pluralTranslation: $localize`agent configurations`,
  },
  [FormName.playlist]: {
    isMasculine: false,
    translation: $localize`playlist`,
    pluralTranslation: $localize`playlists`,
  },
  [FormName.incident]: {
    isMasculine: true,
    translation: $localize`incident`,
    pluralTranslation: $localize`incidents`,
  },
};

export function getFormNameLabel(
  form: FormName,
  pluralForm?: boolean,
  capitalize?: boolean
): string {
  const features = formNamesFeatures[form];
  if (!features) return '';
  let label = features.translation;
  if (pluralForm && features.pluralTranslation) {
    label = features.pluralTranslation;
  }
  return capitalize ? label.capitalize() : label;
}
